import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import {
  catchError,
  defaultIfEmpty,
  firstValueFrom,
  Subject,
  takeUntil,
  throwError,
  timeout,
} from 'rxjs';

import {
  Domain,
  UserAccessProfile,
  UserProfileUpdate,
  canAccessDomain,
  isGlobalAdmin,
  normalizeUserAccessProfile,
} from './user-access.model';
import { UserAccessApiService } from './user-access-api.service';

@Injectable({ providedIn: 'root' })
export class UserAccessService {
  private readonly api = inject(UserAccessApiService);
  private readonly currentUserState = signal<UserAccessProfile | null>(null);
  private readonly loadingState = signal(false);
  private readonly loadedState = signal(false);
  private readonly errorState = signal<string | null>(null);
  private readonly profileSyncWarningState = signal<string | null>(null);
  private loadingPromise: Promise<void> | null = null;
  private readonly cancelInitialization = new Subject<void>();

  readonly currentUser = this.currentUserState.asReadonly();
  readonly loading = this.loadingState.asReadonly();
  readonly loaded = this.loadedState.asReadonly();
  readonly error = this.errorState.asReadonly();
  readonly profileSyncWarning = this.profileSyncWarningState.asReadonly();
  readonly isGlobalAdmin = computed(() => isGlobalAdmin(this.currentUserState()));

  canAccess(domain: Domain): boolean {
    return canAccessDomain(this.currentUserState(), domain);
  }

  initialize(profile?: UserProfileUpdate | null): Promise<void> {
    if (this.loadingPromise) {
      return this.loadingPromise;
    }

    this.loadingState.set(true);
    this.errorState.set(null);
    if (profile !== undefined) {
      this.profileSyncWarningState.set(
        profile
          ? null
          : 'Your name and email could not be loaded from the identity provider. Stored profile details have been retained.',
      );
    }
    const request = profile
      ? this.api.syncProfile(profile).pipe(
          timeout(10_000),
          catchError((error: unknown) => {
            if (
              error instanceof HttpErrorResponse &&
              (error.status === 401 || error.status === 403)
            ) {
              return throwError(() => error);
            }
            this.profileSyncWarningState.set(
              'Your name and email could not be synchronized. Stored profile details have been retained.',
            );
            return this.api.getMe();
          }),
        )
      : this.api.getMe();
    this.loadingPromise = firstValueFrom(
      request.pipe(takeUntil(this.cancelInitialization), defaultIfEmpty(undefined)),
    )
      .then((user) => {
        if (user) {
          this.currentUserState.set(normalizeUserAccessProfile(user));
        } else {
          this.currentUserState.set(null);
          this.errorState.set('Your access profile loading was cancelled.');
        }
      })
      .catch((error: unknown) => {
        this.currentUserState.set(null);
        this.errorState.set(
          error instanceof HttpErrorResponse && error.status === 401
            ? 'Your session is no longer authenticated. Sign in again.'
            : error instanceof HttpErrorResponse && error.status === 403
              ? 'Access to your profile was denied.'
              : 'Your access profile could not be loaded.',
        );
      })
      .finally(() => {
        this.loadingState.set(false);
        this.loadedState.set(true);
        this.loadingPromise = null;
      });

    return this.loadingPromise;
  }

  cancelInitializationRequest(): void {
    if (this.loadingState()) this.cancelInitialization.next();
  }

  retry(): Promise<void> {
    this.loadedState.set(false);
    return this.initialize();
  }

  refreshIfCurrent(user: UserAccessProfile): void {
    if (this.currentUserState()?.id === user.id) {
      this.currentUserState.set(normalizeUserAccessProfile(user));
      void this.retry();
    }
  }
}
